import React, { useEffect, useMemo, useState } from "react";

const PHOTO_PACKAGE_LINK_LIFESPAN_DAYS = 180;
const PHOTO_PACKAGE_LINK_LIFESPAN_MS = PHOTO_PACKAGE_LINK_LIFESPAN_DAYS * 24 * 60 * 60 * 1000;

const formatExpiryDate = (enrollment) => {
  const deliveredAtRaw =
    enrollment?.photoPackage?.deliveredAt ||
    enrollment?.photoPackage?.updatedAt ||
    null;

  if (!deliveredAtRaw) return "";

  const deliveredAt = new Date(deliveredAtRaw);
  if (Number.isNaN(deliveredAt.getTime())) return "";

  const expiresAt = new Date(deliveredAt.getTime() + PHOTO_PACKAGE_LINK_LIFESPAN_MS);
  return expiresAt.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const isLinkStillActive = (enrollment) => {
  const deliveryLink = String(enrollment?.photoPackage?.deliveryLink || "").trim();
  if (!deliveryLink) return false;
  if (!enrollment?.photoPackage?.paid) return false;

  const deliveredAtRaw =
    enrollment?.photoPackage?.deliveredAt ||
    enrollment?.photoPackage?.updatedAt ||
    null;

  if (!deliveredAtRaw) return true;

  const deliveredAt = new Date(deliveredAtRaw);
  if (Number.isNaN(deliveredAt.getTime())) return true;

  return Date.now() - deliveredAt.getTime() <= PHOTO_PACKAGE_LINK_LIFESPAN_MS;
};

const PhotoPackageReadyPanel = ({ enrollments = [] }) => {
  const optedInPhotoPackages = (Array.isArray(enrollments) ? enrollments : []).filter((enr) => {
    const selection = String(enr?.photoPackage?.selection || "").toLowerCase();
    return selection === "opt_in" && !!enr?.photoPackage?.paid;
  });

  const readyPhotoPackages = optedInPhotoPackages.filter((enr) => isLinkStillActive(enr));
  const pendingPhotoPackages = optedInPhotoPackages.filter((enr) => {
    const deliveryLink = String(enr?.photoPackage?.deliveryLink || "").trim();
    return !deliveryLink;
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(!readyPhotoPackages.length);
  }, [readyPhotoPackages.length, pendingPhotoPackages.length]);

  const readySummary = useMemo(() => {
    if (!readyPhotoPackages.length) return "";
    if (readyPhotoPackages.length === 1) {
      return readyPhotoPackages[0]?.course?.title || readyPhotoPackages[0]?.course?.key || "Ready";
    }
    return `${readyPhotoPackages.length} packages ready`;
  }, [readyPhotoPackages]);

  if (!readyPhotoPackages.length && !pendingPhotoPackages.length) return null;

  return (
    <div className="dash-panel">
      <button
        type="button"
        className="dash-photo-ready-toggle"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <span className="dash-photo-ready-toggle-main">
          <span className="dash-panel-title">Your Photo Package</span>
          {!!readyPhotoPackages.length && (
            <span className="dash-photo-ready-toggle-status">
              <span className="dash-photo-ready-toggle-orb" aria-hidden="true">
                <span className="dash-photo-ready-toggle-orb-core" />
                <span className="dash-photo-ready-toggle-orb-ring" />
              </span>
              <span className="dash-photo-ready-toggle-summary">{readySummary}</span>
            </span>
          )}
          {!readyPhotoPackages.length && !!pendingPhotoPackages.length && (
            <span className="dash-panel-subtitle">Your photo package will appear here as soon as it is uploaded.</span>
          )}
        </span>
        <span className={`dash-photo-ready-toggle-caret ${isOpen ? "is-open" : ""}`}>▾</span>
      </button>

      {!!readyPhotoPackages.length && (
        <div className="dash-photo-ready-banner">Photo Package Ready ✦</div>
      )}

      {isOpen && !!readyPhotoPackages.length && (
        <>
          {readyPhotoPackages.map((enr) => {
            const courseTitle = enr?.course?.title || enr?.course?.key || "Your course";
            const deliveryLink = String(enr?.photoPackage?.deliveryLink || "").trim();
            const expiresLabel = formatExpiryDate(enr);

            return (
              <a
                key={String(enr?._id || courseTitle)}
                className="dash-photo-ready-card"
                href={deliveryLink}
                target="_blank"
                rel="noreferrer"
              >
                <div className="dash-photo-ready-top">
                  <div className="dash-photo-ready-title">Your photos are ready!</div>
                  <div className="dash-photo-ready-sub">{courseTitle}</div>
                </div>

                <div className="dash-photo-ready-mid">
                  <div className="dash-photo-ready-perk">
                    <div className="dash-photo-ready-perk-label">Included in your package</div>
                    <div className="dash-photo-ready-bullets">
                      <div className="dash-photo-ready-bullet">Edited work photos</div>
                      <div className="dash-photo-ready-bullet">Edited headshots</div>
                      <div className="dash-photo-ready-bullet">All raw headshots</div>
                    </div>
                  </div>

                  <div className="dash-photo-ready-note">
                    Additional headshots can be professionally edited in the same style for $5 per photo.
                  </div>

                  <div className="dash-photo-ready-save-note">
                    {expiresLabel
                      ? `Link is valid until ${expiresLabel}. After that, it will be removed, so save everything to your own devices.`
                      : "Save everything to your own devices. This link will be removed later."}
                  </div>
                </div>

                <div className="dash-photo-ready-cta">
                  <span>Open photo package</span>
                  <span className="dash-photo-ready-arrow">→</span>
                </div>
              </a>
            );
          })}
        </>
      )}

    </div>
  );
};

export default PhotoPackageReadyPanel;
